import { ref, computed, onMounted, watch } from 'vue';
import moment from 'moment';
import { contractType } from '@/assets/data/optionData.js';

export function useSummaryData(api, axios) {
  const availableWalletAmount = ref(0);
  const previousMonthMileage = ref(0);
  const topupSum = ref(0);
  const transSum = ref(0);
  const transferredSum = ref(0);
  const deductedSum = ref(0);
  const usedSum = ref(0);
  const thisMonthTransferredSum = ref(0);
  const thisMonthUsedSum = ref(0);
  const previousMonthTransferredSum = ref(0);
  const previousMonthUsedSum = ref(0);
  const scheduleEvents = ref([]);
  const title = ref(document.title || 'Summary');
  const insufficientPointsPopup = ref(false);
  const contactPopup = ref(false);

  const periodTransferTypes = [
    { text: 'Weekly', value: 'WEEKLY' },
    { text: 'Monthly', value: 'MONTHLY' },
    { text: 'Quarterly', value: 'QUARTERLY' },
    { text: 'Annually', value: 'YEARLY' },
    { text: 'One-Time', value: 'SPECIAL_DAY' },
  ];
  const employeeTypes = contractType;

  const comparedToLastMonthAvailable = computed(() => {
    const current = availableWalletAmount.value;
    const prev = previousMonthMileage.value;
    if (!current || !prev) return 0;
    return Math.round(((current - prev) / prev) * 100);
  });

  const comparedToLastMonthTransferred = computed(() => {
    const curr = -thisMonthTransferredSum.value;
    const prev = -previousMonthTransferredSum.value;
    if (!curr || !prev) return 0;
    return Math.round(((curr - prev) / prev) * 100);
  });

  const comparedToLastMonthUsed = computed(() => {
    const curr = -thisMonthUsedSum.value;
    const prev = -previousMonthUsedSum.value;
    if (!curr || !prev) return 0;
    return Math.round(((curr - prev) / prev) * 100);
  });

  const getAvailablePoint = async () => {
    try {
      const res = await axios.get(api('uri', 'get-availablePoint'));
      availableWalletAmount.value = res.data.data.availableWalletAmount;
      previousMonthMileage.value = res.data.data.previousMonthMileage;
    } catch (err) {
      console.error(err);
    }
  };

  const getTotalSummary = async () => {
    try {
      const res = await axios.get(api('uri', 'get-total-summary'));
      const d = res.data.data;
      topupSum.value = d.topupSum;
      transSum.value = d.transSum;
      transferredSum.value = d.transferredSum;
      deductedSum.value = d.deductedSum;
      usedSum.value = d.usedSum;
      thisMonthTransferredSum.value = d.thisMonthTransferredSum || 0;
      thisMonthUsedSum.value = d.thisMonthUsedSum || 0;
      previousMonthTransferredSum.value = d.previousMonthTransferredSum || 0;
      previousMonthUsedSum.value = d.previousMonthUsedSum || 0;
    } catch (err) {
      console.error(err);
    }
  };

  const getEventOfMonth = async () => {
    try {
      const res = await axios.get(api('uri', 'get-event-of-month'));
      scheduleEvents.value = res.data.data.scheduleEvents;
    } catch (err) {
      console.error(err);
    }
  };

  const pointShortageCalculate = () => {
    let wallet = availableWalletAmount.value;
    scheduleEvents.value?.forEach(schedule => {
      if (schedule.transferExecuteStatus === 'READY') {
        wallet += schedule.totalMileageVolume;
        schedule.pointShortage = wallet < 0;
      }
    });
  };

  const numberWithCommas = (number) => {
    return number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '0';
  };

  const fmtTimezoneToMMddyyyy = (tz) => {
    return tz ? moment(tz, 'YYYY-MM-DDTHH:mm:ssZ').format('MM/DD/YYYY') : '';
  };

  const fmtTimezoneToMMddyyyyHHmm = (tz) => {
    return tz ? moment(tz, 'YYYY-MM-DDTHH:mm:ssZ').format('MM/DD/YYYY HH:mm') :'';
  };

  const fmtTimezoneToHHmm = (tz) => {
    return tz ? moment(tz, 'YYYY-MM-DDTHH:mm:ssZ').format('HH:mm') :'';
  };

  const setEmpType = (jsonStr) => {
    return JSON.parse(jsonStr)
      .filter(({ employeeType, checked }) => checked && employeeType !== 'ETC')
      .map(({ employeeType }) => employeeType);
  };

  const allocationTermsConverter = (value) => {
    const map = {
      SAME: 'All Equal',
      JOB_TITLE: 'Tier Based',
      DEPARTMENT: 'Team Based'
    };
    return map[value] || '';
  };

  const insufficientPoints = () => {
    insufficientPointsPopup.value = true;
  };

  const openContactPopup = () => {
    insufficientPointsPopup.value = false;
    contactPopup.value = true;
  };

  const convertPeriodTransferTypeName = (code) => {
    return periodTransferTypes.find(t => t.value === code)?.text || code;
  };

  const convertEmployeeTypeName = (code) => {
    return employeeTypes.find(t => t.value === code)?.text || code;
  };

  // Add loadTooltipElementReady before onMounted
  const loadTooltipElementReady = (el) => {
    if (!el) return;
  };

  const sortByInsufficientThenDate = () => {
    scheduleEvents.value.sort((a, b) => {
      const aIsInsufficient = a.pointShortage === true;
      const bIsInsufficient = b.pointShortage === true;

      if (aIsInsufficient === bIsInsufficient) {
        return new Date(a.finishExecuteDateTime) - new Date(b.finishExecuteDateTime);
      }

      return aIsInsufficient ? -1 : 1;
    });
  };

  const sortedScheduleEvents = computed(() => {
    const isInsufficient = (event) => event.pointShortage === true;
    const getDate = (event) => new Date(event.finishExecuteDateTime || event.executeDatetime);

    return [...scheduleEvents.value].sort((a, b) => {
      const aIsInsufficient = isInsufficient(a);
      const bIsInsufficient = isInsufficient(b);

      if (aIsInsufficient && !bIsInsufficient) {
        return -1;
      }

      if (!aIsInsufficient && bIsInsufficient) {
        return 1;
      }

      return getDate(a) - getDate(b);
    });
  });

  onMounted(() => {
    getAvailablePoint();
    getTotalSummary();
    getEventOfMonth();
  });

  watch([availableWalletAmount, scheduleEvents], pointShortageCalculate);

  return {
    title,
    availableWalletAmount,
    previousMonthMileage,
    topupSum,
    transSum,
    transferredSum,
    deductedSum,
    usedSum,
    thisMonthTransferredSum,
    thisMonthUsedSum,
    previousMonthTransferredSum,
    previousMonthUsedSum,
    scheduleEvents,
    sortedScheduleEvents,
    insufficientPointsPopup,
    contactPopup,
    periodTransferTypes,
    employeeTypes,
    comparedToLastMonthAvailable,
    comparedToLastMonthTransferred,
    comparedToLastMonthUsed,
    numberWithCommas,
    fmtTimezoneToMMddyyyy,
    fmtTimezoneToMMddyyyyHHmm,
    fmtTimezoneToHHmm,
    setEmpType,
    allocationTermsConverter,
    insufficientPoints,
    openContactPopup,
    convertPeriodTransferTypeName,
    convertEmployeeTypeName,
    loadTooltipElementReady,
    sortByInsufficientThenDate
  };
}